/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet';
import { apiUrl, authApi, httpClient, landingPagesApi } from 'api';
import { LoginPayload } from 'types/payloads/LoginPayload';
import TextField from 'components/_common/forms/TextField';
import ValidatePassword from 'components/_common/forms/ValidatePassword';
import GoogleIcon from 'assets/icons/google-logo.svg';
import OfficeIcon from 'assets/icons/office-logo.svg';
import { useAuth } from 'contexts/authContext';
import { useGlobal } from 'contexts/globalContext';
import { AuthData } from 'types/Auth';
import { Button } from 'components/_new/Button';
import { useInterval } from 'primereact/hooks';
import { MessageBox } from 'components/_new/MessageBox';
import { InputCheckbox } from 'components/_new/InputCheckbox';
import { generateAutocompleteId } from 'utils/generate-autocomplete-id';
import i18n from 'locale';
import LanguageSwitcher from './LanguageSwitcher';

interface LoginFormObject {
	login: string;
	password: string;
	code?: number;
	remember2fa?: boolean;
}

const Login: FC = () => {
	const { t } = useTranslation();
	// const [persistLogin, setPersistLogin] = useState<boolean>(false);
	const { toastRef } = useGlobal();
	const { authenticate, isLogged, rememberToken } = useAuth();

	const { search } = useLocation();
	const queryParams = Object.fromEntries(Array.from(new URLSearchParams(search).entries()));

	const navigate = useNavigate();
	const { control, handleSubmit, setError, reset, getValues, clearErrors } = useForm<LoginFormObject>({
		defaultValues: {
			login: '',
			password: '',
			code: undefined,
			remember2fa: undefined,
		},
	});

	const [twoFactorAuthMode, setTwoFactorAuthMode] = useState<{ code: 0 | 1 | 2 | 3; uuid: string | null }>({
		code: 0,
		uuid: null,
	});
	const handleCancelTwoFactorAuth = useCallback(() => {
		setTwoFactorAuthMode({ code: 0, uuid: null });
		reset();
	}, []);

	const refineMessage = (message: string) => {
		return message === 'Internal server error' || message === 'Invalid credentials'
			? t('misc.forms.notMatchingPassword')
			: message;
	};

	const { mutateAsync: joinProgramMutateAsync } = useMutation(
		(data: { organizationName: string; programName: string }) =>
			landingPagesApi.joinProgram(data.organizationName, data.programName, true),
		{
			onSuccess: () => {
				// toastRef?.current?.show({ severity: 'success', detail: 'Dołączyłeś do programu' });
			},
		}
	);

	const { mutate, isLoading: isSubmitting } = useMutation((data: LoginPayload) => authApi.loginUser(data), {
		onMutate: () => {
			clearErrors();
		},
		onSuccess: (response) => {
			if (Object.hasOwn(response, 'message')) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any, prefer-destructuring
				const message = (response as any).message?.code;
				const uuid = (response as any).message?.uuid;
				if (message === '2FA_USER_REQUIRED') {
					setTwoFactorAuthMode({ code: 1, uuid });
				} else if (message === '2FA_PROGRAM_REQUIRED') {
					setTwoFactorAuthMode({ code: 2, uuid });
				} else if (message === '2FA_ADMIN_REQUIRED') {
					setTwoFactorAuthMode({ code: 3, uuid });
				}
			} else {
				const { refreshToken, accessToken, rememberToken } = response as AuthData;
				window.localStorage.removeItem('externalLoginType');

				authenticate({ refreshToken, accessToken, rememberToken }, () => {
					toastRef?.current?.show({ severity: 'success', detail: t('auth.loggedSuccessfully') });

					const programNameFromLocalStorage = window.localStorage.getItem('programName');
					if (programNameFromLocalStorage) {
						const [organizationName, programName] = programNameFromLocalStorage.split('/');
						joinProgramMutateAsync({ organizationName, programName }).then(() => {
							navigate({ pathname: '/', search });
							window.localStorage.removeItem('programName');
						});
					} else {
						navigate({ pathname: '/', search });
					}
				});
			}
		},
		onError: ({ response }) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any, prefer-destructuring
			const message = response?.data?.message;
			const logout = response?.data?.logout || false;
			if (Object.hasOwn(response.data, 'message') && typeof message === 'string') {
				switch (message) {
					case 'Invalid credentials':
						setError('login', {
							type: 'custom',
							message: refineMessage(response.data.message),
						});
						setError('password', {
							type: 'custom',
							message: refineMessage(response.data.message),
						});
						break;
					case 'Invalid verification code':
						setError('code', { message: t('auth.twoFactorAuthInvalidCode') });
						break;
					case 'Verification code expires':
						setError('code', { message: t('auth.twoFactorAuthInvalidExpires') });
						break;
					case 'Account login temporary blocked':
						setError('login', { message: t('auth.loginTempBlocked') });
						break;
					case 'Limit max invalid code enter':
						toastRef?.current?.show({ severity: 'error', detail: t('auth.tooMany2faAttempts') });
						setTwoFactorAuthMode({ code: 0, uuid: null });
						break;
					default:
						break;
				}
			} else {
				setError('login', {
					type: 'custom',
					message: refineMessage(response.data.message),
				});
				setError('password', {
					type: 'custom',
					message: refineMessage(response.data.message),
				});
			}

			if (logout) {
				setTwoFactorAuthMode({ code: 0, uuid: null });
				reset();
			}
		},
	});

	const externalLogin = (type: string) => {
		if (queryParams?.redirectUrl) {
			window.localStorage.setItem(`${type}-auth-redirect`, queryParams.redirectUrl);
		}
		const language = i18n.language.startsWith('pl') ? 'pl' : 'en';
		window.location.href = `${apiUrl}/auth/${type}/redirect?lng=${language}`;
	};

	const handleExternalLoginClick = (type: string) => {
		externalLogin(type);
	};

	const handleSubmitForm = handleSubmit(({ login, password, code, remember2fa }) => {
		const payload = {
			email: login.trim(),
			password,
			code: twoFactorAuthMode.code > 0 ? Number(code) : undefined,
			uuid: twoFactorAuthMode.code > 0 ? twoFactorAuthMode.uuid : undefined,
			remember2fa: twoFactorAuthMode.code > 0 ? Boolean(remember2fa) : undefined,
			rememberToken: rememberToken || undefined,
		};
		mutate(payload);
	});

	const handleResend = () => {
		const { login, password } = getValues();
		setTimer(0);
		mutate({ email: login.trim(), password });
	};

	const [timer, setTimer] = useState(0);
	const countdown = timer <= 60 ? 60 - timer : 0;
	useInterval(() => setTimer((prev) => prev + 1), 1000, twoFactorAuthMode.code > 0);

	if (isLogged && !queryParams.logout) {
		return <Navigate to={{ pathname: '/', search }} />;
	}

	return (
		<>
			<Helmet title={t('auth.login')} />
			<form onSubmit={handleSubmitForm} className="flex flex-column row-gap-3" autoComplete="off">
				{twoFactorAuthMode.code > 0 && (
					<>
						<div>
							<Trans as="p" t={t} i18nKey="auth.twoFactorAuthInfo" components={[<strong />]} />
							{twoFactorAuthMode.code === 2 && (
								<MessageBox
									variant="purple"
									highlight
									message={t('userPanel.myAccount.twoFactorAuthProgramForced')}
								/>
							)}
							{twoFactorAuthMode.code === 3 && (
								<MessageBox
									variant="purple"
									highlight
									message={t('userPanel.myAccount.twoFactorAuthAdminForced')}
								/>
							)}
						</div>

						<TextField
							name="code"
							control={control}
							required
							placeholder={t('auth.twoFactorAuthCode')}
							inputClassName="text-2xl text-center bold"
							maxLength={6}
							rules={{
								validate: {
									isNumber: (value) => {
										const num = Number(value);
										if (!value || Number.isNaN(num)) {
											return t('auth.twoFactorAuthRInvalidInput');
										}
										return true;
									},
								},
							}}
						/>
						<div className="flex flex-row gap-2 justify-content-between">
							<Button
								label={t('actions.cancel')}
								onClick={handleCancelTwoFactorAuth}
								variant="primary-text"
								variantSize="sm"
							/>
							<Button
								label={`${t('auth.twoFactorAuthResend')}${countdown > 0 ? ` (${countdown}s)` : ''}`}
								onClick={handleResend}
								variant="primary-outlined"
								disabled={timer < 60}
								variantSize="sm"
							/>
						</div>

						<Controller
							control={control}
							name="remember2fa"
							render={({ field }) => (
								<InputCheckbox
									label={t('auth.twoFactorAuthRememberDevice')}
									checked={Boolean(field.value)}
									onChange={(event) => field.onChange(Boolean(event.checked))}
									onBlur={field.onBlur}
									inputRef={field.ref}
								/>
							)}
						/>
					</>
				)}

				{twoFactorAuthMode.code === 0 && (
					<>
						<TextField
							name="login"
							placeholder={t('auth.inputs.login')}
							control={control}
							required
							autoComplete={generateAutocompleteId('login')}
						/>
						<ValidatePassword
							name="password"
							placeholder={t('auth.inputs.password')}
							control={control}
							required
							autoComplete={generateAutocompleteId('password')}
						/>
					</>
				)}

				<Button submit label={t('auth.buttons.login')} loading={isSubmitting} block />

				<div className="text-sm text-center underline">
					<Button
						label={t('auth.resetPassword')}
						variant="primary-text"
						onClick={() => navigate('/password-reset')}
					/>
				</div>

				<span className="text-center">{t('misc.or')}</span>

				<div className="flex justify-content-between gap-2">
					<Button
						onClick={() => handleExternalLoginClick('google')}
						variant="primary-outlined"
						style={{ borderRadius: '12px' }}
						label={t('auth.buttons.loginWithGoogle')}
						block
						className="p-1 py-2"
					>
						<img src={GoogleIcon} alt="icon" />
						<span style={{ fontSize: '0.7rem' }}>{t('auth.buttons.loginWithGoogle')}</span>
					</Button>
					<Button
						onClick={() => handleExternalLoginClick('microsoft')}
						variant="primary-outlined"
						style={{ borderRadius: '12px' }}
						label={t('auth.buttons.loginWithOffice')}
						block
						className="p-1 py-2"
					>
						<img src={OfficeIcon} alt="icon" />
						<span style={{ fontSize: '0.7rem' }}>{t('auth.buttons.loginWithOffice')}</span>
					</Button>
				</div>
				<div className="flex justify-content-center mt-4">
					<LanguageSwitcher />
				</div>
			</form>
		</>
	);
};

export default Login;
